import type { PostMention } from '@sirocco/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { identifiantDuPseudo } from '../../firebase/annuaire';
import { normalizeUsername } from '../../firebase/paths';
import { nomsPossibles, splitMentions } from '../../utils/mentions';
import { useSession } from '../../store/SessionContext';
import { profilePath } from '../../utils/routes';

/**
 * Le contenu d'un post, mentions comprises.
 *
 * Une mention se detache par sa couleur et mene au profil. Deux choses
 * la rendent possible : la liste rangee a cote du texte au moment de
 * publier, et — pour tout le reste — l'annuaire des pseudos, interroge
 * ici.
 *
 * # Pourquoi les deux
 *
 * La liste dit qui l'auteur a cite, donc qui a ete prevenu : elle vit
 * avec le post et ne bouge plus. Mais elle ne se remplissait qu'en
 * touchant une suggestion, si bien que les posts d'avant portent des
 * pseudos morts — bonne orthographe, aucun lien. Rien ne pouvait les
 * rattraper, puisque l'affichage ne cherchait rien.
 *
 * Il cherche desormais. Un pseudo qui existe devient un lien, quel que
 * soit l'age du post et la facon dont il a ete ecrit.
 *
 * Ce que cela n'ouvre toujours pas : un arobase colle a un mot reste une
 * adresse, et un pseudo mal orthographie reste du texte. On ne devine
 * pas qui l'auteur visait.
 */
function useMentionsEtendues(content: string, mentions: PostMention[]): PostMention[] {
  const [etendues, setEtendues] = useState(mentions);
  /*
    La dependance porte sur ce que la liste contient, pas sur le tableau
    qui la porte. Un appelant qui construirait le sien a chaque rendu
    relancerait sinon l'effet, qui reposerait un nouveau tableau, qui
    relancerait l'effet : une boucle qu'aucun ecran ne declenche
    aujourd'hui, et qu'un refactor ailleurs ouvrirait sans bruit.
  */
  const signature = mentions.map((mention) => mention.id).join('|');

  useEffect(() => {
    const connus = new Set(mentions.map((mention) => normalizeUsername(mention.username)));
    const groupes = nomsPossibles(content);
    if (groupes.length === 0) {
      setEtendues(mentions);
      return;
    }

    let vivant = true;
    void Promise.all(
      /*
        Toutes les decoupes, de la plus longue a la plus courte, et la
        premiere qui repond gagne.

        N'en essayer qu'une ne pouvait pas marcher : « @Ninja taga tu
        viens » propose d'abord la phrase entiere, qui ne correspond a
        personne, et c'est la troisieme decoupe qui porte le pseudo. Les
        reponses sont gardees pour la session, donc les decoupes
        perdantes ne se paient qu'une fois.
      */
      groupes.map(async (formes) => {
        for (const forme of formes) {
          // Deja dans la liste rangee avec le post : rien a chercher.
          if (connus.has(normalizeUsername(forme))) return null;
          const uid = await identifiantDuPseudo(forme);
          if (uid) return { id: uid, username: forme };
        }
        return null;
      }),
    ).then((reponses) => {
      if (!vivant) return;
      const ajouts = reponses.filter(
        (mention): mention is PostMention =>
          mention !== null && !mentions.some((autre) => autre.id === mention.id),
      );
      setEtendues(ajouts.length ? [...mentions, ...ajouts] : mentions);
    });

    return () => {
      vivant = false;
    };
    // `mentions` est lu a l'interieur : sa signature suffit a dire
    // quand le relire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, signature]);

  return etendues;
}

export function PostText({
  content,
  mentions,
  className = 'post__content',
}: {
  content: string;
  mentions: PostMention[];
  /** Chaque ecran a sa propre classe de contenu : fil, profil, commentaires. */
  className?: string;
}) {
  const { user } = useSession();
  const citees = useMentionsEtendues(content, mentions);

  return (
    <p className={`${className} texte-libre`}>
      {splitMentions(content, citees).map((piece, index) =>
        piece.kind === 'mention' ? (
          <Link
            key={index}
            className="mention"
            to={profilePath(piece.mention.id, user?.id)}
            /* Le post entier est cliquable : la mention garde son clic. */
            onClick={(event) => event.stopPropagation()}
          >
            {piece.text}
          </Link>
        ) : (
          piece.text
        ),
      )}
    </p>
  );
}
